import { useEffect, useMemo, useState } from "react";
import { X as XIcon, Loader2, Check, AlertTriangle, Plus, Wrench, User, Users, ClipboardList, Camera, Package, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoUpload from "@/components/PhotoUpload";

/**
 * PpfComposeModal — a 1:1 clone of the mobile app's PPF submission screen.
 *
 * Fields are grouped into the same sections the mobile app uses so a planner
 * reading a PPF on either device recognises the shape immediately:
 *
 *   1. Priority + Asset
 *   2. Problem Description
 *   3. Who — tradesman name/trade/crew (auto-filled from profile, editable)
 *   4. Timing — SMU, target completion, estimated hours, headcount
 *   5. Skill — specialist skill + human resources list
 *   6. Parts Needed — dynamic list of {part_number, part_name, quantity, store_location}
 *   7. Tooling Required — chip list
 *   8. Send-to supervisor + notes
 *   9. Photos — reference photos + parts book photos
 *   10. "Submit for approval" (optional — otherwise saves as draft)
 *
 * Every field maps to the exact key the backend `PPFIn` schema expects, so
 * portal-submitted PPFs and mobile-submitted PPFs land in the same fields.
 * Fixes Josh's complaint that "the tradesman etc. isn't showing" — it's now
 * captured, sent, and displayed.
 */

const PRIORITIES = [
  { code: "critical", short: "P1", label: "CRITICAL", color: "#f87171", bg: "rgba(248,113,113,0.15)" },
  { code: "high",     short: "P2", label: "HIGH",     color: "#fbbf24", bg: "rgba(251,191,36,0.15)" },
  { code: "medium",   short: "P3", label: "MEDIUM",   color: "#f5b942", bg: "rgba(245,185,66,0.15)" },
  { code: "low",      short: "P4", label: "LOW",      color: "#8bb4ff", bg: "rgba(139,180,255,0.15)" },
];

export default function PpfComposeModal({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [assets, setAssets] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [refsLoading, setRefsLoading] = useState(true);

  // Form state — every key maps to the backend PPFIn schema.
  const [form, setForm] = useState(() => ({
    priority: "medium",
    asset_id: "",
    fault: "",
    tradesman_name: user?.name || user?.username || "",
    tradesman_trade: user?.trade || "",
    crew_name: user?.crew_name || "",
    smu: "",
    target_completion: "",
    estimated_hours: "",
    num_people: "",
    specialist_skill: "",
    // trades_needed: [{ trade: string, hours: number|"" }] — replaces the
    // freeform human_resources chip list so Josh's planners can see
    // exactly which trades are needed AND how many hours each. Legacy
    // `human_resources_list` + `human_resources` are still populated on
    // submit for backward compatibility with mobile clients.
    trades_needed: [],
    parts_needed: [], // [{ part_number, part_name, quantity, store_location }]
    tooling_required: [], // string[]
    other_requirements: [], // string[]
    target_supervisor_id: "",
    target_supervisor_name: "",
    notes: "",
    photos: [], // reference
    parts_book_photos: [],
    submit_after_create: true,
  }));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Refresh tradesman defaults if the user context resolves after mount.
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      tradesman_name: f.tradesman_name || user.name || user.username || "",
      tradesman_trade: f.tradesman_trade || user.trade || "",
      crew_name: f.crew_name || user.crew_name || "",
    }));
  }, [user]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setRefsLoading(true);
      const [a, u] = await Promise.allSettled([
        api.get("/assets"),
        api.get("/admin/users").catch(() => ({ data: [] })),
      ]);
      const list = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : (d?.items || d?.results || d?.data || []);
      };
      setAssets(list(a).filter((x) => !x.retired));
      const users = list(u).filter((x) => {
        const role = String(x.role || "").toLowerCase();
        return /(supervisor|superintendent|owner|planner|control)/.test(role);
      });
      setSupervisors(users);
      setRefsLoading(false);
    })().catch(() => setRefsLoading(false));
  }, [open]);

  const canSubmit = useMemo(() => {
    if (!form.asset_id) return false;
    if (!String(form.fault || "").trim()) return false;
    return true;
  }, [form.asset_id, form.fault]);

  const addPart = () =>
    set("parts_needed", [...(form.parts_needed || []), { part_number: "", part_name: "", quantity: "", store_location: "" }]);
  const updatePart = (i, key, value) => {
    const arr = [...(form.parts_needed || [])];
    arr[i] = { ...arr[i], [key]: value };
    set("parts_needed", arr);
  };
  const removePart = (i) => set("parts_needed", form.parts_needed.filter((_, idx) => idx !== i));

  // Trades × Hours — planners need to see WHICH trades and HOW MANY HOURS
  // for each. Replaces the freeform HR chip list.
  const addTrade = () =>
    set("trades_needed", [...(form.trades_needed || []), { trade: "", hours: "" }]);
  const updateTrade = (i, key, value) => {
    const arr = [...(form.trades_needed || [])];
    arr[i] = { ...arr[i], [key]: value };
    set("trades_needed", arr);
  };
  const removeTrade = (i) => set("trades_needed", form.trades_needed.filter((_, idx) => idx !== i));

  const addChip = (fieldName, value) => {
    const v = String(value || "").trim();
    if (!v) return;
    const existing = form[fieldName] || [];
    if (existing.includes(v)) return;
    set(fieldName, [...existing, v]);
  };
  const removeChip = (fieldName, idx) => {
    const arr = [...(form[fieldName] || [])];
    arr.splice(idx, 1);
    set(fieldName, arr);
  };

  const submit = async () => {
    if (!canSubmit) {
      setMsg({ type: "err", text: "Asset and problem description are required." });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      const chosenSup = supervisors.find((s) => String(s.id || s._id) === String(form.target_supervisor_id));
      const payload = {
        asset_id: form.asset_id,
        fault: form.fault.trim(),
        priority: form.priority,
        // People
        tradesman_name: form.tradesman_name.trim() || undefined,
        tradesman_trade: form.tradesman_trade.trim() || undefined,
        crew_name: form.crew_name.trim() || undefined,
        // Timing
        smu: form.smu.trim() || undefined,
        target_completion: form.target_completion || undefined,
        estimated_hours: form.estimated_hours ? Number(form.estimated_hours) : undefined,
        num_people: form.num_people ? Number(form.num_people) : undefined,
        // Skill — trades × hours (the new authoritative model). Legacy
        // fields are derived from it so mobile clients that read the older
        // shape still see the trades on their screens.
        specialist_skill: form.specialist_skill.trim() || undefined,
        trades_needed: (form.trades_needed || []).filter((t) => t.trade),
        human_resources_list: (form.trades_needed || [])
          .filter((t) => t.trade)
          .map((t) => (t.hours ? `${t.trade} · ${t.hours}h` : String(t.trade))),
        human_resources: (form.trades_needed || [])
          .filter((t) => t.trade)
          .map((t) => (t.hours ? `${t.trade} · ${t.hours}h` : String(t.trade)))
          .join(", ") || undefined,
        // Parts + tooling
        parts_needed: (form.parts_needed || []).filter((p) => p.part_number || p.part_name),
        tooling_required: (form.tooling_required || []).filter(Boolean),
        other_requirements: (form.other_requirements || []).filter(Boolean),
        // Routing
        target_supervisor_id: chosenSup ? (chosenSup.id || chosenSup._id) : undefined,
        target_supervisor_name: chosenSup ? (chosenSup.name || chosenSup.username) : undefined,
        // Notes + photos
        notes: form.notes.trim() || undefined,
        photos: (form.photos || []).map((p) => p.data || p),
        parts_book_photos: (form.parts_book_photos || []).map((p) => p.data || p),
        // Flow
        submit_after_create: !!form.submit_after_create,
        client_op_id: `ppf-${Date.now()}`,
      };
      const { data } = await api.post("/ppfs", payload);
      setMsg({ type: "ok", text: form.submit_after_create ? "Planning Request submitted for supervisor approval." : "Planning Request saved as draft." });
      setTimeout(() => onCreated?.(data), 600);
    } catch (ex) {
      setMsg({ type: "err", text: apiError(ex) });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ppf-compose-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <ClipboardList className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">New Planning Request</div>
            <div className="font-display font-black text-xl tracking-tight">Log a Planning Request · same layout as mobile</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose} data-testid="ppf-compose-close"><XIcon className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {refsLoading && <div className="pill !text-xs !py-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading assets & supervisors…</div>}

          {/* Priority chip selector — matches mobile P1-P4 pills */}
          <Section title="Priority">
            <div className="grid grid-cols-4 gap-2" data-testid="ppf-priority">
              {PRIORITIES.map((p) => {
                const on = form.priority === p.code;
                return (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => set("priority", p.code)}
                    className="rounded-xl py-2.5 px-2 transition-all"
                    style={{
                      background: on ? p.bg : "rgba(255,255,255,0.03)",
                      border: `1.5px solid ${on ? p.color : "rgba(255,255,255,0.08)"}`,
                      color: on ? p.color : "rgba(255,255,255,0.55)",
                      transform: on ? "scale(1.03)" : "none",
                    }}
                    data-testid={`ppf-priority-${p.code}`}
                  >
                    <div className="font-display font-black text-lg leading-none tabular-nums">{p.short}</div>
                    <div className="text-[9px] uppercase tracking-widest font-black mt-1">{p.label}</div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Asset picker */}
          <Section title="Asset *">
            <select
              className="input"
              value={form.asset_id}
              onChange={(e) => set("asset_id", e.target.value)}
              data-testid="ppf-asset"
            >
              <option value="">— Select asset —</option>
              {assets.map((a) => (
                <option key={a.id || a._id} value={a.id || a._id}>
                  {a.id || a._id}{a.name ? ` · ${a.name}` : ""}{a.asset_type ? ` · ${a.asset_type}` : ""}
                </option>
              ))}
            </select>
          </Section>

          {/* Problem Description */}
          <Section title="Problem Description *">
            <textarea
              className="textarea"
              rows={3}
              placeholder="What's wrong? What needs to happen?"
              value={form.fault}
              onChange={(e) => set("fault", e.target.value)}
              data-testid="ppf-fault"
            />
          </Section>

          {/* Who — tradesman, trade, crew */}
          <Section title="Who" icon={User}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Labelled label="Tradesman">
                <input
                  className="input" value={form.tradesman_name}
                  onChange={(e) => set("tradesman_name", e.target.value)}
                  placeholder="e.g. Josh Argall"
                  data-testid="ppf-tradesman"
                />
              </Labelled>
              <Labelled label="Trade">
                <input
                  className="input" value={form.tradesman_trade}
                  onChange={(e) => set("tradesman_trade", e.target.value)}
                  placeholder="e.g. Fitter"
                  data-testid="ppf-tradesman-trade"
                />
              </Labelled>
              <Labelled label="Crew">
                <input
                  className="input" value={form.crew_name}
                  onChange={(e) => set("crew_name", e.target.value)}
                  placeholder="e.g. B Crew"
                  data-testid="ppf-crew"
                />
              </Labelled>
            </div>
          </Section>

          {/* Timing */}
          <Section title="Timing" icon={Clock}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Labelled label="SMU">
                <input className="input tabular-nums" value={form.smu} onChange={(e) => set("smu", e.target.value)} placeholder="hours" data-testid="ppf-smu" />
              </Labelled>
              <Labelled label="Target Completion">
                <input type="date" className="input" value={form.target_completion} onChange={(e) => set("target_completion", e.target.value)} data-testid="ppf-target" style={{ colorScheme: "dark" }} />
              </Labelled>
              <Labelled label="Est. Hours">
                <input type="number" step="0.5" min="0" className="input tabular-nums" value={form.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} placeholder="e.g. 6" data-testid="ppf-est-hours" />
              </Labelled>
              <Labelled label="No. of People">
                <input type="number" min="1" className="input tabular-nums" value={form.num_people} onChange={(e) => set("num_people", e.target.value)} placeholder="e.g. 2" data-testid="ppf-people" />
              </Labelled>
            </div>
          </Section>

          {/* Trades × Hours — the "at a glance" grid Josh's planners
              asked for. One row per trade with its own hours estimate.
              Replaces the legacy human-resources chip list. */}
          <Section title="Required Trades &amp; Est. Hours" icon={Users}>
            <Labelled label="Specialist skill required (optional)">
              <input className="input" value={form.specialist_skill} onChange={(e) => set("specialist_skill", e.target.value)} placeholder="e.g. Certified welder, HV licence" data-testid="ppf-skill" />
            </Labelled>
            {(form.trades_needed || []).length === 0 && (
              <div className="text-xs text-muted mb-2">
                No trades added yet — tap below. Add each trade separately with its own hours estimate (e.g. Boilermaker · 4h, Fitter · 2h).
              </div>
            )}
            <div className="space-y-2" data-testid="ppf-trades-list">
              {(form.trades_needed || []).map((t, i) => (
                <div
                  key={i}
                  className="panel-flat p-3 grid grid-cols-[1fr_100px_auto] gap-2 items-center"
                  data-testid={`ppf-trade-row-${i}`}
                >
                  <input
                    className="input !py-2"
                    placeholder="Trade (e.g. Boilermaker, Fitter, Auto-Elec)"
                    value={t.trade || ""}
                    onChange={(e) => updateTrade(i, "trade", e.target.value)}
                    data-testid={`ppf-trade-name-${i}`}
                  />
                  <div className="relative">
                    <input
                      className="input !py-2 tabular-nums text-right pr-8"
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="4"
                      value={t.hours || ""}
                      onChange={(e) => updateTrade(i, "hours", e.target.value)}
                      data-testid={`ppf-trade-hours-${i}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest text-muted pointer-events-none">hrs</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost !px-2 self-center text-red-400"
                    onClick={() => removeTrade(i)}
                    title="Remove trade"
                    data-testid={`ppf-trade-remove-${i}`}
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-sm mt-2 flex items-center gap-2"
              onClick={addTrade}
              style={{ background: "rgba(245,130,32,0.08)", border: "1px dashed rgba(245,130,32,0.5)", color: "var(--primary)" }}
              data-testid="ppf-add-trade"
            >
              <Plus className="w-4 h-4" /> Add another trade
            </button>
            {(form.trades_needed || []).length > 0 && (
              <div className="mt-2 text-[10px] uppercase tracking-widest text-muted flex items-center gap-2">
                Total est. across trades:{" "}
                <span className="font-black text-white tabular-nums">
                  {(form.trades_needed || []).reduce((sum, t) => sum + (Number(t.hours) || 0), 0)}
                </span>{" "}
                hrs
              </div>
            )}
          </Section>

          {/* Parts Needed — dynamic list */}
          <Section title="Parts Needed" icon={Package}>
            {(form.parts_needed || []).length === 0 && (
              <div className="text-xs text-muted mb-2">No parts added yet — tap the button below to add one.</div>
            )}
            <div className="space-y-2">
              {(form.parts_needed || []).map((p, i) => (
                <div
                  key={i}
                  className="panel-flat p-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_80px_1fr_auto] gap-2"
                  data-testid={`ppf-part-row-${i}`}
                >
                  <input className="input !py-2" placeholder="Part number" value={p.part_number || ""} onChange={(e) => updatePart(i, "part_number", e.target.value)} data-testid={`ppf-part-number-${i}`} />
                  <input className="input !py-2" placeholder="Part name / description" value={p.part_name || ""} onChange={(e) => updatePart(i, "part_name", e.target.value)} data-testid={`ppf-part-name-${i}`} />
                  <input className="input !py-2 tabular-nums" placeholder="Qty" value={p.quantity || ""} onChange={(e) => updatePart(i, "quantity", e.target.value)} data-testid={`ppf-part-qty-${i}`} />
                  <input className="input !py-2" placeholder="Store location (aisle/bin)" value={p.store_location || ""} onChange={(e) => updatePart(i, "store_location", e.target.value)} data-testid={`ppf-part-store-${i}`} />
                  <button
                    className="btn btn-sm btn-ghost !px-2 self-center text-red-400"
                    onClick={() => removePart(i)}
                    title="Remove part"
                    data-testid={`ppf-part-remove-${i}`}
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-sm mt-2 flex items-center gap-2"
              onClick={addPart}
              style={{ background: "rgba(245,130,32,0.08)", border: "1px dashed rgba(245,130,32,0.5)", color: "var(--primary)" }}
              data-testid="ppf-add-part"
            >
              <Plus className="w-4 h-4" /> Add another part
            </button>
          </Section>

          {/* Tooling + Other requirements */}
          <Section title="Tooling / Requirements" icon={Wrench}>
            <ChipList
              label="Tooling required"
              placeholder="e.g. Hydraulic press, torque wrench…"
              values={form.tooling_required || []}
              onAdd={(v) => addChip("tooling_required", v)}
              onRemove={(i) => removeChip("tooling_required", i)}
              testid="ppf-tooling-list"
            />
            <ChipList
              label="Other requirements"
              placeholder="e.g. Isolation, crane, permits…"
              values={form.other_requirements || []}
              onAdd={(v) => addChip("other_requirements", v)}
              onRemove={(i) => removeChip("other_requirements", i)}
              testid="ppf-other-list"
            />
          </Section>

          {/* Send to supervisor + notes */}
          <Section title="Send To">
            <Labelled label="Supervisor">
              <select
                className="input"
                value={form.target_supervisor_id}
                onChange={(e) => set("target_supervisor_id", e.target.value)}
                data-testid="ppf-supervisor"
              >
                <option value="">— Any supervisor —</option>
                {supervisors.map((s) => (
                  <option key={s.id || s._id} value={s.id || s._id}>
                    {(s.name || s.username)}{s.role ? ` · ${s.role}` : ""}
                  </option>
                ))}
              </select>
            </Labelled>
            <Labelled label="Notes">
              <textarea className="textarea" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="ppf-notes" placeholder="Anything else the planner should know" />
            </Labelled>
          </Section>

          {/* Photos */}
          <Section title="Photos" icon={Camera}>
            <PhotoUpload
              value={form.photos}
              onChange={(v) => set("photos", v)}
              max={8}
              label="Reference photos (of the fault)"
              testid="ppf-photos"
            />
            <div className="mt-3">
              <PhotoUpload
                value={form.parts_book_photos}
                onChange={(v) => set("parts_book_photos", v)}
                max={8}
                label="Parts book photos (page + diagram)"
                testid="ppf-parts-book-photos"
              />
            </div>
          </Section>

          {/* Submit-immediately toggle */}
          <label className="panel-flat p-3 flex items-center gap-3 cursor-pointer" data-testid="ppf-submit-toggle">
            <input
              type="checkbox"
              checked={!!form.submit_after_create}
              onChange={(e) => set("submit_after_create", e.target.checked)}
              className="w-5 h-5"
            />
            <div>
              <div className="font-display font-black text-sm">Submit for supervisor approval now</div>
              <div className="text-[11px] text-muted">Uncheck to save as a draft you can finish later.</div>
            </div>
          </label>

          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`} data-testid="ppf-compose-msg">
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 flex items-center justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSubmit || busy}
            style={{ minHeight: 48, fontSize: 15, padding: "0 22px" }}
            data-testid="ppf-submit"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
            {form.submit_after_create ? "Submit Planning Request" : "Save Draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest font-black text-muted flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />}
        {title}
      </div>
      {children}
    </section>
  );
}

function Labelled({ label, children }) {
  return (
    <div className="field !mb-2">
      <label className="text-[10px]">{label}</label>
      {children}
    </div>
  );
}

function ChipList({ label, placeholder, values = [], onAdd, onRemove, testid }) {
  const [draft, setDraft] = useState("");
  const commit = () => { if (draft.trim()) { onAdd(draft.trim()); setDraft(""); } };
  return (
    <div className="field !mb-2" data-testid={testid}>
      <label className="text-[10px]">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={`${testid}-chips`}>
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="pill !text-[11px] !py-1 !px-2"
            style={{ background: "rgba(245,130,32,0.12)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.35)" }}
          >
            {v}
            <button className="ml-1 hover:text-red-400" onClick={() => onRemove(i)} data-testid={`${testid}-remove-${i}`} title="Remove">
              <XIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className="input flex-1"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
          placeholder={placeholder}
          data-testid={`${testid}-input`}
        />
        <button
          type="button"
          className="btn btn-sm"
          onClick={commit}
          style={{ background: "rgba(245,130,32,0.12)", border: "1px solid rgba(245,130,32,0.35)", color: "var(--primary)" }}
          data-testid={`${testid}-add`}
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
    </div>
  );
}
